"use client"

import {TrendingUp} from "lucide-react"
import {Cell, Pie, PieChart, ResponsiveContainer} from "recharts"
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/components/ui/card"
import {ChartConfig, ChartContainer} from "@/components/ui/chart"
import {useEffect, useState} from "react"
import axiosInstance from "@/api/axios"
import {useTranslation} from "react-i18next"

export const description = "Bin usage statistics with donut chart"

// Define interfaces for our data model
interface BinUsageStats {
    used: number
    openedNotUsed: number
    totalOpens: number
}

interface BinUsageRecord {
    binId: string
    timestamp: Date
    wasUsed: boolean
    wasteType?: string | null
}

interface WasteTypeDistribution {
    sub_class_waste_type: string
    count: number
}

interface BinUsageData {
    timePeriod: string
    stats: BinUsageStats
    records: BinUsageRecord[]
    wasteTypes?: WasteTypeDistribution[]
}

// BinUsageService class for data fetching and processing
class BinUsageService {
    // Method to fetch data from the API
    static async fetchData(timeRange?: string | number, bins?: string[]): Promise<BinUsageData> {
        try {
            // Make the API call using the recommended params approach
            const response = await axiosInstance.get("operation_page_bin_usage_stats", {
                params: {
                    timeRange: timeRange || 'Year',
                    bins: bins?.length ? bins.join(",") : undefined,
                },
                paramsSerializer: {
                    indexes: null,
                },
            });

            // Process the response data
            let data: BinUsageData;

            if (typeof response.data === "string") {
                try {
                    data = JSON.parse(response.data);
                } catch (err) {
                    console.error("Error parsing JSON string:", err);
                    throw new Error("Invalid data format received from API");
                }
            } else {
                data = response.data;
            }

            // Transform date strings into Date objects
            if (data.records) {
                data.records = data.records.map((record: any) => ({
                    ...record,
                    timestamp: new Date(record.timestamp)
                }));
            }

            return data;
        } catch (error) {
            console.error("Error fetching bin usage data:", error);

            // Fallback to mock data if API fails
            return this.generateMockData(timeRange, bins);
        }
    }

    // Generate realistic mock data based on the inputs (fallback method)
    private static generateMockData(timeRange?: string | number, bins?: string[]): BinUsageData {
        const currentYear = new Date().getFullYear();

        // Determine the selected year if timeRange is a year
        let selectedYear = currentYear;
        if (typeof timeRange === 'number' || (typeof timeRange === 'string' && !isNaN(parseInt(timeRange)))) {
            selectedYear = typeof timeRange === 'number' ? timeRange : parseInt(timeRange);
        }

        const binCount = bins?.length || 5;

        // Determine time period based on parameters
        let timePeriod: string;
        if (typeof timeRange === 'number' || (typeof timeRange === 'string' && !isNaN(parseInt(timeRange)))) {
            timePeriod = `Year ${selectedYear}`;
        } else {
            timePeriod = timeRange?.toString() || 'Year';
        }

        // Generate random records with realistic distribution
        const records: BinUsageRecord[] = [];
        const numberOfRecords = 150 + Math.floor(Math.random() * 100); // Between 150-250 records

        // Usage rate varies by year (older years might have lower usage rates)
        const baseUsageRate = 0.6 + (Math.random() * 0.2); // 60-80% base rate
        const yearFactor = 1 - (Math.max(0, currentYear - selectedYear) * 0.05); // Decrease by 5% per year in the past
        const actualUsageRate = baseUsageRate * yearFactor;

        // Waste type options (excluding 'nothing' as it's a special value)
        const wasteTypeOptions = ["paper", "plastic", "glass", "metal", "organic", "paper bottle", "plastic cup"];

        // Create records
        for (let i = 0; i < numberOfRecords; i++) {
            const binIndex = Math.floor(Math.random() * binCount);
            const binId = bins?.[binIndex] || `bin-${binIndex + 1}`;

            // Create timestamp within the year
            const month = Math.floor(Math.random() * 12);
            const day = Math.floor(Math.random() * 28) + 1;
            const timestamp = new Date(selectedYear, month, day);

            // Determine if bin was used based on usage rate
            const wasUsed = Math.random() < actualUsageRate;

            records.push({
                binId,
                timestamp,
                wasUsed,
                wasteType: wasUsed ? wasteTypeOptions[Math.floor(Math.random() * wasteTypeOptions.length)] : 'nothing'
            });
        }

        // Calculate stats from records
        const used = records.filter(r => r.wasUsed).length;
        const openedNotUsed = records.length - used;

        // Generate waste type distribution (excluding 'nothing')
        const wasteTypeMap: Record<string, number> = {};
        records.forEach(record => {
            if (record.wasUsed && record.wasteType && record.wasteType !== 'nothing') {
                wasteTypeMap[record.wasteType] = (wasteTypeMap[record.wasteType] || 0) + 1;
            }
        });

        const wasteTypeDistributions: WasteTypeDistribution[] = Object.entries(wasteTypeMap).map(([type, count]) => ({
            sub_class_waste_type: type,
            count
        })).sort((a, b) => b.count - a.count);

        return {
            timePeriod,
            stats: {
                used,
                openedNotUsed,
                totalOpens: records.length
            },
            records,
            wasteTypes: wasteTypeDistributions
        };
    }

    // Helper method to process data for chart display
    static prepareChartData(data: BinUsageData, t: (key: string) => string) {
        return [
            {
                name: t('binUsageStats.usageCount'),
                value: data.stats.used,
                fill: "hsl(var(--chart-1))",
                percentage: Math.round(
                    (data.stats.used / data.stats.totalOpens) * 100
                ),
            },
            {
                name: t('binUsageStats.openedNotUsed'),
                value: data.stats.openedNotUsed,
                fill: "hsl(var(--chart-3))",
                percentage: Math.round(
                    (data.stats.openedNotUsed / data.stats.totalOpens) * 100
                ),
            },
        ];
    }
}

// Define interface for component props
interface BinUsageStatsProps {
    className?: string
    title?: string
    timeRange?: string | number
    bins?: string[]
}

export function BinUsageStats({
                                  className = "",
                                  title,
                                  timeRange,
                                  bins,
                              }: BinUsageStatsProps) {
    const {t} = useTranslation();

    // Chart configuration - using translations
    const chartConfig = {
        used: {
            label: t('binUsageStats.usageCount'),
            color: "hsl(var(--chart-1))",
        },
        openedNotUsed: {
            label: t('binUsageStats.openedNotUsed'),
            color: "hsl(var(--chart-3))",
        },
    } satisfies ChartConfig;

    // State for loading and data
    const [isLoading, setIsLoading] = useState(true);
    const [usageData, setUsageData] = useState<BinUsageData | null>(null);
    const [chartData, setChartData] = useState<any[]>([]);
    const [usageRate, setUsageRate] = useState(0);
    const [error, setError] = useState<string | null>(null);

    // Fetch data when component mounts or when timeRange/bins change
    useEffect(() => {
        const fetchUsageData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await BinUsageService.fetchData(timeRange, bins);

                // Validate the data
                if (!data || !data.stats || typeof data.stats.used !== 'number' ||
                    typeof data.stats.openedNotUsed !== 'number' ||
                    typeof data.stats.totalOpens !== 'number') {
                    throw new Error(t('binUsageStats.invalidDataStructure'));
                }

                setUsageData(data);
                setChartData(BinUsageService.prepareChartData(data, t));
                setUsageRate(Math.round(
                    (data.stats.used / data.stats.totalOpens) * 100 || 0
                ));
            } catch (error) {
                console.error("Error processing bin usage data:", error);
                setError(t('binUsageStats.failedToLoadStats'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsageData();
    }, [timeRange, bins, t]);

    // Get translated title
    const displayTitle = title || t('binUsageStats.title');

    // Error state
    if (error) {
        return (
            <Card className={`flex flex-col ${className}`}>
                <CardHeader className="items-center pb-4 pt-5">
                    <CardTitle className="text-lg">{displayTitle}</CardTitle>
                    <CardDescription className="text-red-500">{t('binUsageStats.error')}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 pb-6 px-6 flex items-center justify-center">
                    <p className="text-center text-red-500">{error}</p>
                </CardContent>
            </Card>
        );
    }

    // Loading state
    if (isLoading || !usageData) {
        return (
            <Card className={`flex flex-col ${className}`}>
                <CardHeader className="items-center pb-4 pt-5">
                    <CardTitle className="text-lg">{displayTitle}</CardTitle>
                    <CardDescription>{t('binUsageStats.loading')}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 pb-6 px-6 flex items-center justify-center">
                    <div className="animate-pulse w-40 h-40 rounded-full bg-gray-200"></div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={`flex flex-col ${className}`}>
            <CardHeader className="items-center pb-4 pt-5">
                <CardTitle className="text-lg">{displayTitle}</CardTitle>
                <CardDescription>{t(`binUsageStats.timePeriod.${String(timeRange)?.toLowerCase()}`)}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-6 px-6">
                {/* Central Donut Chart Section */}
                <div className="flex flex-col items-center justify-center">
                    <ChartContainer
                        config={chartConfig}
                        className="w-full h-[280px]"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={80}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill}/>
                                    ))}
                                </Pie>
                                <text
                                    x="50%"
                                    y="50%"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    className="fill-foreground text-4xl font-bold"
                                >
                                    {usageRate}%
                                </text>
                                <text
                                    x="50%"
                                    y="50%"
                                    dy={24}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    className="fill-muted-foreground text-sm"
                                >
                                    {t('binUsageStats.usageRate')}
                                </text>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartContainer>

                    {/* Legend */}
                    <div className="flex gap-8 mt-5 pt-2 pb-1">
                        <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-green-500"></div>
                            <span className="text-sm font-medium">{t('binUsageStats.legend.usageCount')}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-gray-400"></div>
                            <span className="text-sm font-medium">{t('binUsageStats.legend.notUsed')}</span>
                        </div>
                    </div>
                </div>

                {/* Total Opens Info */}
                <div className="text-center mt-6 pt-3 border-t">
                    <p className="text-sm text-muted-foreground">
                        {t('binUsageStats.totalOpensInfo', {count: usageData.stats.totalOpens})}
                    </p>
                </div>
            </CardContent>

            {/* Key Metrics Section */}
            <div className="px-6 pt-2 pb-4">
                <div className="grid grid-cols-2 gap-4">
                    <div
                        className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800/50">
                        <div>
                            <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                {t('binUsageStats.usageCount')}
                            </p>
                            <p className="text-2xl font-bold text-green-800 dark:text-green-300 mt-1">
                                {usageData.stats.used}
                            </p>
                        </div>
                        <div className="w-5 h-5 rounded-full bg-green-500 dark:bg-green-400"></div>
                    </div>

                    <div
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('binUsageStats.openedNotUsed')}
                            </p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                {usageData.stats.openedNotUsed}
                            </p>
                        </div>
                        <div className="w-5 h-5 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                    </div>
                </div>
            </div>

            <CardFooter className="flex-col gap-3 text-sm pt-4 pb-5 border-t px-6">
                <div className="flex items-center gap-2 leading-none font-medium text-center">
                    {usageRate.toFixed(1)}% {t('binUsageStats.binOpeningsResultInUsage')}{" "}
                    <TrendingUp className="h-4 w-4 ml-1"/>
                </div>
                <div className="text-muted-foreground leading-none text-center text-xs">
                    {t('binUsageStats.trackingEfficiency')} {t(`binUsageStats.timePeriod.${String(timeRange)?.toLowerCase()}`)}{" "}
                </div>
            </CardFooter>
        </Card>
    )
}